import React from 'react';
import { useReveal } from '../hooks/useReveal';

// Process section (Fase 2). Distills the per-service `process` arrays from
// data/services.js into one shared 4-step narrative. A calm, light editorial
// section with hairline borders so it contrasts with the dark testimonials
// card below it, while keeping the oversized per-step accent numbers.
const STEPS = [
  {
    no: '01',
    title: 'Discover',
    body: 'A short discovery call to map goals, scope and budget. I leave with a clear brief and you leave with a fixed plan — no vague promises.',
    accent: '#b1c1ef',
  },
  {
    no: '02',
    title: 'Design & build',
    body: 'Direction in Figma, then into build. Wireframes, look-dev and motion come together so you see the thing taking shape, not just slideware.',
    accent: '#f2acac',
  },
  {
    no: '03',
    title: 'Refine',
    body: 'Structured revision rounds where we sweat the details — spacing, timing, copy and performance — until every frame feels intentional.',
    accent: '#ffdd94',
  },
  {
    no: '04',
    title: 'Launch',
    body: 'Ship it properly: exports, handover, on-page SEO and post-launch support so the work keeps performing long after delivery.',
    accent: '#8ea78f',
  },
];

const ProcessSection = () => {
  const ref = useReveal();

  return (
    <section
      ref={ref}
      className="bg-surface text-mainkleur py-20 sm:py-24 lg:py-32 border-t border-hairline"
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 sm:mb-20">
          <div className="flex flex-col gap-5">
            <span className="eyebrow text-muted">How we work</span>
            <h2 className="font-anton uppercase text-[14vw] sm:text-[88px] lg:text-[120px] leading-[0.9] tracking-[-3px] lg:tracking-[-6px]">
              The process
            </h2>
          </div>
          <p className="font-space text-muted text-[15px] sm:text-[17px] lg:text-[20px] leading-[1.5] max-w-[460px] lg:text-right">
            A calm, transparent path from first call to launch. Same backbone whether
            it's a logo, a brand system, a motion piece or a full custom site.
          </p>
        </div>

        <div className="h-px w-full bg-hairline mb-2" />

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-px bg-hairline border border-hairline">
          {STEPS.map((step) => (
            <article
              key={step.no}
              className="reveal-up group relative bg-surface p-7 sm:p-8 lg:p-9 flex flex-col gap-5 min-h-[280px] sm:min-h-[300px] overflow-hidden"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-16 -right-12 w-44 h-44 rounded-full blur-[70px] opacity-25 group-hover:opacity-50 transition-opacity duration-500"
                style={{ backgroundColor: step.accent }}
              />
              <span
                className="section-number text-[64px] sm:text-[76px] lg:text-[84px]"
                style={{ color: step.accent }}
              >
                {step.no}
              </span>
              <h3 className="font-bricolage font-extrabold text-mainkleur text-[24px] sm:text-[28px] tracking-tight">
                {step.title}
              </h3>
              <p className="font-space text-muted text-[14px] sm:text-[15px] leading-[1.55]">
                {step.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
